import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { NoteNaming } from '@/music/i18n/format';

/** Per-device preferences. Persisted locally (see app/store.ts). */
export interface SettingsState {
  /** Letters or syllables for note names; null follows the interface language. */
  noteNaming: NoteNaming | null;
  /** 0..1 */
  volume: number;
  /** Sound delay of this device, subtracted from tap times. Set by calibration. */
  latencyOffsetMs: number;
  /** Set terms apart in texts and explain them on hover: one switch for both. */
  termHints: boolean;
}

export const initialSettings: SettingsState = {
  noteNaming: null,
  volume: 0.8,
  latencyOffsetMs: 0,
  termHints: true,
};

/** Calibration results outside this range are a mistake, not a device. */
export const LATENCY_RANGE = { min: -80, max: 450 } as const;

const settingsSlice = createSlice({
  name: 'settings',
  initialState: initialSettings,
  reducers: {
    noteNamingChanged(state, action: PayloadAction<NoteNaming | null>) {
      state.noteNaming = action.payload;
    },
    volumeChanged(state, action: PayloadAction<number>) {
      state.volume = Math.min(1, Math.max(0, action.payload));
    },
    termHintsChanged(state, action: PayloadAction<boolean>) {
      state.termHints = action.payload;
    },
    latencyCalibrated(state, action: PayloadAction<number>) {
      const rounded = Math.round(action.payload);
      state.latencyOffsetMs = Math.min(LATENCY_RANGE.max, Math.max(LATENCY_RANGE.min, rounded));
    },
  },
});

export const { noteNamingChanged, volumeChanged, latencyCalibrated, termHintsChanged } = settingsSlice.actions;
export const settingsReducer = settingsSlice.reducer;

export interface WithSettings {
  settings: SettingsState;
}

export const selectSettings = (state: WithSettings) => state.settings;

export function isSettingsState(value: unknown): value is SettingsState {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Partial<SettingsState>;
  return (
    typeof candidate.volume === 'number' &&
    typeof candidate.latencyOffsetMs === 'number' &&
    // Absent in settings saved before the switch existed: it is then on, as for a new device.
    (candidate.termHints === undefined || typeof candidate.termHints === 'boolean') &&
    (candidate.noteNaming === null ||
      candidate.noteNaming === 'letters' ||
      candidate.noteNaming === 'solfege')
  );
}
